import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { 
  Search, 
  Filter, 
  X, 
  Plus, 
  SlidersHorizontal,
  Sparkles,
  Save,
  RotateCcw,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * SmartSearch Component
 * 
 * Props:
 * - data: Array of objects to search/filter
 * - columns: Array of column definitions { key, label, type, options }
 *   - type: 'text' | 'number' | 'date' | 'select' | 'boolean'
 *   - options: For select type, array of { value, label }
 * - onFilter: Callback with filtered data
 * - quickFilters: Array of preset filters { label, filters: { key: value } }
 * - placeholder: Search input placeholder
 * - showAdvanced: Show advanced filters button (default true)
 */
export default function SmartSearch({
  data = [],
  columns = [],
  onFilter,
  quickFilters = [],
  placeholder = "Search across all fields...",
  showAdvanced = true,
  className
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);

  // Filter operators by type
  const operators = {
    text: [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals' },
      { value: 'starts', label: 'Starts with' },
      { value: 'ends', label: 'Ends with' },
      { value: 'empty', label: 'Is empty' },
      { value: 'notEmpty', label: 'Is not empty' },
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'gt', label: 'Greater than' },
      { value: 'gte', label: 'Greater or equal' },
      { value: 'lt', label: 'Less than' },
      { value: 'lte', label: 'Less or equal' },
      { value: 'between', label: 'Between' },
    ],
    date: [
      { value: 'equals', label: 'On' },
      { value: 'before', label: 'Before' },
      { value: 'after', label: 'After' },
      { value: 'between', label: 'Between' },
      { value: 'today', label: 'Today' },
      { value: 'thisWeek', label: 'This week' },
      { value: 'thisMonth', label: 'This month' },
    ],
    select: [
      { value: 'equals', label: 'Is' },
      { value: 'notEquals', label: 'Is not' },
      { value: 'in', label: 'Is any of' },
    ],
    boolean: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ]
  };

  // Smart search across all text fields
  const smartSearch = (items, term) => {
    if (!term.trim()) return items;
    
    const lowerTerm = term.toLowerCase();
    
    return items.filter(item => {
      // Search across all columns
      return columns.some(col => {
        const value = getNestedValue(item, col.key);
        if (value === null || value === undefined) return false;
        
        const strValue = String(value).toLowerCase();
        return strValue.includes(lowerTerm);
      });
    });
  };

  // Get nested value from object (e.g., "client.name")
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => 
      current && current[key] !== undefined ? current[key] : null, obj
    );
  };

  // Apply a single filter condition
  const applyFilter = (item, filter) => {
    const value = getNestedValue(item, filter.key);
    const filterValue = filter.value;
    const type = columns.find(c => c.key === filter.key)?.type || 'text';

    switch (filter.operator) {
      // Text operators
      case 'contains':
        return String(value || '').toLowerCase().includes(String(filterValue).toLowerCase());
      case 'equals':
        if (type === 'number') return Number(value) === Number(filterValue);
        return String(value || '').toLowerCase() === String(filterValue).toLowerCase();
      case 'starts':
        return String(value || '').toLowerCase().startsWith(String(filterValue).toLowerCase());
      case 'ends':
        return String(value || '').toLowerCase().endsWith(String(filterValue).toLowerCase());
      case 'empty':
        return !value || value === '';
      case 'notEmpty':
        return value && value !== '';
      
      // Number operators
      case 'gt':
        return Number(value) > Number(filterValue);
      case 'gte':
        return Number(value) >= Number(filterValue);
      case 'lt':
        return Number(value) < Number(filterValue);
      case 'lte':
        return Number(value) <= Number(filterValue);
      case 'between':
        const [min, max] = filterValue.split(',').map(Number);
        return Number(value) >= min && Number(value) <= max;
      
      // Select operators
      case 'notEquals':
        return String(value || '').toLowerCase() !== String(filterValue).toLowerCase();
      case 'in':
        const values = filterValue.split(',').map(v => v.trim().toLowerCase());
        return values.includes(String(value || '').toLowerCase());
      
      // Date operators
      case 'before':
        return new Date(value) < new Date(filterValue);
      case 'after':
        return new Date(value) > new Date(filterValue);
      case 'today':
        const today = new Date().toDateString();
        return new Date(value).toDateString() === today;
      case 'thisWeek':
        const now = new Date();
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        const weekEnd = new Date(now.setDate(now.getDate() - now.getDay() + 6));
        const dateValue = new Date(value);
        return dateValue >= weekStart && dateValue <= weekEnd;
      case 'thisMonth':
        const thisMonth = new Date();
        const monthStart = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
        const monthEnd = new Date(thisMonth.getFullYear(), thisMonth.getMonth() + 1, 0);
        return new Date(value) >= monthStart && new Date(value) <= monthEnd;
      
      // Boolean
      case 'true':
        return value === true || value === 'true' || value === 1;
      case 'false':
        return value === false || value === 'false' || value === 0 || !value;
      
      default:
        return true;
    }
  };

  // Apply all filters
  const filteredData = useMemo(() => {
    let result = [...data];
    
    // Apply smart search first
    result = smartSearch(result, searchTerm);
    
    // Apply active filters
    activeFilters.forEach(filter => {
      result = result.filter(item => applyFilter(item, filter));
    });
    
    return result;
  }, [data, searchTerm, activeFilters]);

  // Notify parent of filtered data
  useEffect(() => {
    onFilter?.(filteredData);
  }, [filteredData, onFilter]);

  // Add a new filter
  const addFilter = (key, operator, value) => {
    const newFilter = { 
      id: Date.now(), 
      key, 
      operator, 
      value,
      label: columns.find(c => c.key === key)?.label || key
    };
    setActiveFilters(prev => [...prev, newFilter]);
  };

  // Remove a filter
  const removeFilter = (filterId) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setActiveFilters([]);
    setActiveQuickFilter(null);
  };

  // Apply quick filter
  const applyQuickFilter = (quickFilter) => {
    if (activeQuickFilter?.label === quickFilter.label) {
      // Toggle off
      setActiveQuickFilter(null);
      setActiveFilters([]);
    } else {
      setActiveQuickFilter(quickFilter);
      const newFilters = Object.entries(quickFilter.filters).map(([key, value], idx) => ({
        id: Date.now() + idx,
        key,
        operator: 'equals',
        value,
        label: columns.find(c => c.key === key)?.label || key
      }));
      setActiveFilters(newFilters);
    }
  };

  const hasActiveFilters = searchTerm || activeFilters.length > 0;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Main search bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-9"
            data-testid="smart-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {showAdvanced && (
          <Popover open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {activeFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <AdvancedFilterPanel
                columns={columns}
                operators={operators}
                activeFilters={activeFilters}
                onAddFilter={addFilter}
                onRemoveFilter={removeFilter}
                onClose={() => setAdvancedOpen(false)}
              />
            </PopoverContent>
          </Popover>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={clearAllFilters} title="Clear all filters">
            <RotateCcw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Quick filters */}
      {quickFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Quick:
          </span>
          {quickFilters.map((qf, idx) => (
            <Badge
              key={idx}
              variant={activeQuickFilter?.label === qf.label ? "default" : "outline"}
              className="cursor-pointer hover:bg-accent"
              onClick={() => applyQuickFilter(qf)}
            >
              {qf.label}
            </Badge>
          ))}
        </div>
      )}

      {/* Active filters display */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {activeFilters.map(filter => (
            <Badge 
              key={filter.id} 
              variant="secondary"
              className="gap-1 pr-1"
            >
              <span className="font-medium">{filter.label}</span>
              <span className="text-muted-foreground mx-1">{filter.operator}</span>
              <span>"{filter.value}"</span>
              <button
                onClick={() => removeFilter(filter.id)}
                className="ml-1 hover:bg-background/50 rounded p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Results count */}
      {hasActiveFilters && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredData.length} of {data.length} results
        </div>
      )}
    </div>
  );
}

// Advanced Filter Panel Component
function AdvancedFilterPanel({ columns, operators, activeFilters, onAddFilter, onRemoveFilter, onClose }) {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const selectedColumnDef = columns.find(c => c.key === selectedColumn);
  const columnType = selectedColumnDef?.type || 'text';
  const availableOperators = operators[columnType] || operators.text;

  const handleAddFilter = () => {
    if (selectedColumn && selectedOperator && (filterValue || ['empty', 'notEmpty', 'today', 'thisWeek', 'thisMonth', 'true', 'false'].includes(selectedOperator))) {
      onAddFilter(selectedColumn, selectedOperator, filterValue);
      setSelectedColumn('');
      setSelectedOperator('');
      setFilterValue('');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="font-medium flex items-center gap-2">
        <Filter className="w-4 h-4" />
        Advanced Filters
      </div>
      
      <div className="space-y-3">
        <div>
          <label className="text-sm text-muted-foreground">Field</label>
          <Select value={selectedColumn} onValueChange={(v) => { setSelectedColumn(v); setSelectedOperator(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {columns.map(col => (
                <SelectItem key={col.key} value={col.key}>{col.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedColumn && (
          <div>
            <label className="text-sm text-muted-foreground">Condition</label>
            <Select value={selectedOperator} onValueChange={setSelectedOperator}>
              <SelectTrigger>
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                {availableOperators.map(op => (
                  <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedOperator && !['empty', 'notEmpty', 'today', 'thisWeek', 'thisMonth', 'true', 'false'].includes(selectedOperator) && (
          <div>
            <label className="text-sm text-muted-foreground">Value</label>
            {selectedColumnDef?.type === 'select' && selectedColumnDef?.options ? (
              <Select value={filterValue} onValueChange={setFilterValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  {selectedColumnDef.options.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : selectedColumnDef?.type === 'date' ? (
              <Input
                type="date"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              />
            ) : selectedColumnDef?.type === 'number' ? (
              <Input
                type="number"
                placeholder={selectedOperator === 'between' ? "min,max" : "Enter number"}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              />
            ) : (
              <Input
                placeholder="Enter value"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              />
            )}
          </div>
        )}

        <Button 
          onClick={handleAddFilter} 
          disabled={!selectedColumn || !selectedOperator}
          className="w-full"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Filter
        </Button>
      </div>

      {activeFilters.length > 0 && (
        <>
          <div className="border-t pt-3">
            <label className="text-sm text-muted-foreground">Current Filters</label>
            <div className="space-y-2 mt-2">
              {activeFilters.map(filter => (
                <div key={filter.id} className="flex items-center justify-between text-sm bg-muted/50 rounded px-2 py-1">
                  <span>
                    <strong>{filter.label}</strong> {filter.operator} "{filter.value}"
                  </span>
                  <button onClick={() => onRemoveFilter(filter.id)} className="text-muted-foreground hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Export a simpler version for basic use cases
export function SimpleSearch({ value, onChange, placeholder = "Search...", className }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9"
        data-testid="search-input"
      />
    </div>
  );
}
